import { Button, makeStyles, tokens, Tooltip } from '@fluentui/react-components'
import { Compose20Regular, Navigation20Regular } from '@fluentui/react-icons'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ScribblerFullLogo16 from '../../assets/logos/logo-full-16.svg?react'
import { HEADER_HEIGHT } from '../../constants/layout'
import { Dialog } from '../Dialog/Dialog'

const useStyles = makeStyles({
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    color: tokens.colorNeutralForegroundOnBrand,
    minHeight: `${HEADER_HEIGHT}px`,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
  },
  leftActions: {
    display: 'flex',
    alignItems: 'center',
  },
  menuButton: {
    color: tokens.colorNeutralForeground2,
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalSNudge}`,
    ':hover': {
      color: tokens.colorNeutralForeground2Hover,
    },
  },
  navIcon: {
    // navigation icon's visual weight sits higher than Compose
    transform: 'translateY(1px)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
    color: tokens.colorNeutralForeground1,
  },
  logoImage: {
    width: 'auto',
    display: 'block',
  },
})

type HeaderProps = {
  onMenuClick: () => void
  onChatClear: () => void
}

const Header = ({ onMenuClick, onChatClear }: HeaderProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [isClearChatDialogOpen, setIsClearChatDialogOpen] = useState(false)

  const onClearChatDialogOpenChange = useCallback((open: boolean) => {
    setIsClearChatDialogOpen(open)
  }, [])

  const onConfirmClear = useCallback(() => {
    onChatClear()
    setIsClearChatDialogOpen(false)
  }, [onChatClear])

  return (
    <>
      <div className={classes.header}>
        <div className={classes.leftActions}>
          <Tooltip content={t('header.openMenu')} relationship="label" appearance="inverted">
            <Button
              icon={<Navigation20Regular className={classes.navIcon} />}
              appearance="subtle"
              className={classes.menuButton}
              onClick={onMenuClick}
              aria-label={t('header.openMenu')}
            />
          </Tooltip>
          <Tooltip content={t('header.newChat')} relationship="label" appearance="inverted">
            <Button
              icon={<Compose20Regular />}
              appearance="subtle"
              className={classes.menuButton}
              onClick={() => onClearChatDialogOpenChange(true)}
              aria-label={t('header.newChat')}
            />
          </Tooltip>
        </div>
        <div className={classes.logo}>
          <ScribblerFullLogo16
            className={classes.logoImage}
            aria-label={t('nameShort')}
            role="img"
          />
        </div>
      </div>
      <Dialog
        title={t('dialog.clearChatTitle')}
        content={t('dialog.clearChatContent')}
        primaryButtonText={t('header.newChat')}
        secondaryButtonText={t('cancel')}
        onConfirm={onConfirmClear}
        onOpenChange={onClearChatDialogOpenChange}
        isDialogOpen={isClearChatDialogOpen}
      />
    </>
  )
}

export { Header }
